import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider as PolarisAppProvider, Modal, BlockStack, Banner } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';

// Context
import { AppProvider, useAppContext } from './context/AppContext';

// InClub Custom Theme for Polaris
import './styles/inclub-theme.css';

// Pages
import Dashboard from './pages/Dashboard';

// Components
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Routes and Modals (inside all contexts)
function AppContent() {
  const {
    // Authentication state
    isAuthenticated,
    authError,

    // Initialization state
    isInitializing,
    initError,

    // Connection state
    connectionStatus,
    storeApproved,
    storeData,
    inclubData,

    // Modal states
    showReconnectModal,
    setShowReconnectModal,
    errorModalOpen,
    errorModalMessage,
    errorModalTitle,

    // Shop info
    shop,

    // Actions
    initializeApp,
    closeErrorModal
  } = useAppContext();

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={
            <Dashboard
              // Authentication state
              isAuthenticated={isAuthenticated}
              authError={authError}

              // Initialization state
              isInitializing={isInitializing}
              initError={initError}

              // Connection state
              connectionStatus={connectionStatus}
              storeApproved={storeApproved}
              storeData={storeData}
              inclubData={inclubData}

              // Actions
              onRetryConnection={() => initializeApp(shop)}
              onRetryAuth={() => window.location.reload()}
              shop={shop}
            />
          } />
        </Routes>
      </Layout>

      {/* Reconnection Modal */}
      <Modal
        open={showReconnectModal}
        onClose={() => setShowReconnectModal(false)}
        title="Connection Issue"
        primaryAction={{
          content: 'Reconnect',
          onAction: () => {
            // TODO: Handle reconnection
            setShowReconnectModal(false);
          },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowReconnectModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="4">
            <p>There's an issue with your store connection. Please reconnect to continue using InClub.</p>
          {storeData?.connectionError && (
            <Banner status="warning" title="Connection Error">
              <p>{storeData.connectionError}</p>
            </Banner>
          )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Global Error Modal */}
      <Modal
        open={errorModalOpen}
        onClose={closeErrorModal}
        title={errorModalTitle}
        primaryAction={{
          content: 'Close',
          onAction: closeErrorModal
        }}
      >
        <Modal.Section>
          <p>{errorModalMessage}</p>
        </Modal.Section>
      </Modal>
    </>
  );
}

// Root App Component - Fixed hierarchy to prevent AppProvider race condition
function App() {
  return (
    <ErrorBoundary>
      <PolarisAppProvider
        i18n={enTranslations}
        features={{
          newDesignLanguage: true,
        }}
      >
        <Router>
          <AppProvider>
            <AppContent />
          </AppProvider>
        </Router>
      </PolarisAppProvider>
    </ErrorBoundary>
  );
}

export default App;